import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const reminderSchema = z.object({
  email: z.string().trim().email().max(254),
  makeSlug: z.string().trim().min(1).max(80),
  modelSlug: z.string().trim().min(1).max(80),
  vehicleLabel: z.string().trim().min(1).max(120),
  odometer: z.number().int().min(0).max(2_000_000),
  annualMiles: z.number().int().min(500).max(200_000),
  nextService: z.string().trim().min(1).max(120),
  nextDueMiles: z.number().int().min(0).max(2_000_000),
});

/** Days of lead time before the milestone is reached. */
const LEAD_DAYS = 14;

function isoDate(d: Date) {
  return d.toISOString().slice(0, 10);
}

export const createMaintenanceReminder = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => reminderSchema.parse(input))
  .handler(async ({ data }) => {
    const milesToGo = Math.max(data.nextDueMiles - data.odometer, 0);
    const daysToGo = Math.round((milesToGo / data.annualMiles) * 365);
    const dueOn = new Date(Date.now() + daysToGo * 86_400_000);
    const notifyOn = new Date(dueOn.getTime() - LEAD_DAYS * 86_400_000);
    const today = new Date();

    const row = {
      email: data.email.toLowerCase(),
      make_slug: data.makeSlug,
      model_slug: data.modelSlug,
      vehicle_label: data.vehicleLabel,
      odometer: data.odometer,
      annual_miles: data.annualMiles,
      odometer_recorded_on: isoDate(today),
      next_service: data.nextService,
      next_due_miles: data.nextDueMiles,
      next_due_on: isoDate(dueOn),
      notify_on: isoDate(notifyOn > today ? notifyOn : today),
      status: "active" as const,
      last_sent_at: null,
    };

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: existing } = await supabaseAdmin
      .from("maintenance_reminders")
      .select("id")
      .eq("email", row.email)
      .eq("make_slug", row.make_slug)
      .eq("model_slug", row.model_slug)
      .eq("status", "active")
      .maybeSingle();

    if (existing) {
      const { error } = await supabaseAdmin
        .from("maintenance_reminders")
        .update(row)
        .eq("id", existing.id);
      if (error) throw new Error("Could not update your reminder. Please try again.");
    } else {
      const { error } = await supabaseAdmin.from("maintenance_reminders").insert(row);
      if (error) throw new Error("Could not save your reminder. Please try again.");
    }

    // Confirmation email — never fail the save if email is unavailable.
    try {
      const { sendTemplateEmail } = await import("@/lib/email-templates/send-email");
      await sendTemplateEmail("maintenance-reminder-confirmation", row.email, {
        templateData: {
          vehicleLabel: row.vehicle_label,
          service: row.next_service,
          dueMiles: row.next_due_miles,
          notifyOn: row.notify_on,
        },
        idempotencyKey: `reminder-confirm-${row.email}-${row.make_slug}-${row.model_slug}-${row.next_due_miles}`,
      });
    } catch (err) {
      console.error("Reminder confirmation email failed", err);
    }

    return {
      ok: true as const,
      service: data.nextService,
      dueMiles: data.nextDueMiles,
      notifyOn: row.notify_on,
    };
  });